import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { api } from '../lib/api';
import { useLang, KIND_EN } from '../lib/i18n';
import { dayLong, dayShort, hhmm, tl, TIERS, TIER_KEYS, FEE_KURUS } from '../lib/format';
import type { EventDetail } from '../lib/types';
import { ErrorView, Loading } from '../components/AppHeader';
import { Ico, Poster } from '../components/brand';
import { toast } from '../lib/toast';

const AV = ['#E8DCC4', '#D8E0EE', '#EBD3C8', '#D7E6DF', '#E3D6EA', '#EFE3BF'];

export function EventPage() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const { tx, lang } = useLang();
  const [e, setE] = useState<EventDetail | null>(null);
  const [error, setError] = useState<number | null>(null);
  useEffect(() => {
    api<EventDetail>(`/events/${slug}`).then(setE, (x) => setError(x.status || 500));
  }, [slug]);
  if (error) return <ErrorView msg={error === 404 ? tx('Bu etkinlik bulunamadı.', 'Event not found.') : tx('Etkinlik yüklenemedi.', 'Couldn’t load the event.')} action={<button className="btn btn-line" onClick={() => nav('/')}>{tx('Keşfet’e dön', 'Back to discover')}</button>} />;
  if (!e) return <Loading />;
  if (!e.sessions.length) return <ErrorView msg={tx('Bu etkinliğin yaklaşan seansı yok.', 'No upcoming sessions.')} />;

  const sess = e.sessions.find((s) => s.id === params.get('s')) || e.sessions.find((s) => s.status !== 'sold') || e.sessions[0]!;
  const seatsLeft = { few: tx('Son koltuklar', 'Last seats'), ok: tx('Koltuk var', 'Available'), sold: tx('Tükendi', 'Sold out') };
  const cta = e.demand ? tx('Sıraya gir', 'Join the queue') : tx('Koltuk seç', 'Select seats');
  const go = () => {
    if (sess.status === 'sold') return toast(tx('Bu seans tükendi. Başka bir seans seç.', 'This session is sold out. Pick another.'));
    nav(e.demand ? `/sira/${sess.id}` : `/koltuk/${sess.id}`);
  };
  const en = lang === 'en';
  const kind = en ? KIND_EN[e.kind] || e.kind : e.kind;
  const share = async () => {
    const url = location.href;
    try {
      if (navigator.share) await navigator.share({ title: e.title, url });
      else {
        await navigator.clipboard.writeText(url);
        toast(tx('Bağlantı kopyalandı', 'Link copied'));
      }
    } catch {
      /* cancelled */
    }
  };
  const save = () => {
    try {
      const k = 'gise.saved';
      const s = new Set(JSON.parse(localStorage.getItem(k) || '[]'));
      s.add(e.slug);
      localStorage.setItem(k, JSON.stringify([...s]));
    } catch {
      /* ignore */
    }
    toast(tx('Kaydedildi', 'Saved'));
  };
  const free = (k: (typeof TIER_KEYS)[number]) => {
    const n = sess.tiers?.[k]?.free ?? 0;
    return n <= 0 ? tx('Tükendi', 'Sold out') : `${n} ${tx('boş', 'left')}`;
  };

  return (
    <>
      <div className="scroll">
        <div className="ev-layout">
          <div className="hero">
            <Poster poster={e.poster} title={e.short || e.title} kind={kind} venue={e.venue.name} titleSize={e.title.length > 12 ? 52 : 96} />
            <div className="hero-tools">
              <button type="button" className="icon-btn glass" onClick={() => nav('/')} aria-label={tx('Geri', 'Back')}>
                <Ico n="back" />
              </button>
              <div>
                <button type="button" className="icon-btn glass" aria-label={tx('Kaydet', 'Save')} onClick={save}>
                  <Ico n="heart" />
                </button>
                <button type="button" className="icon-btn glass" aria-label={tx('Paylaş', 'Share')} onClick={share}>
                  <Ico n="share" />
                </button>
              </div>
            </div>
          </div>
          <div className="ev-body">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span className="cap muted">
                {kind} · {en ? e.subEn : e.sub}
              </span>
              {e.badge && <span className={`badge ${e.badge.c}`}>{en ? e.badge.en : e.badge.t}</span>}
            </div>
            <h1 className="d-xl" style={{ margin: '10px 0 10px' }}>
              {e.title}
            </h1>
            <p className="t-l" style={{ margin: 0, color: 'var(--ink-2)' }}>
              {en ? e.creditEn || e.credit : e.credit}
            </p>
            <div className="facts">
              <div>
                <div className="cap muted">{tx('Süre', 'Duration')}</div>
                <div className="v">{en ? e.durEn : e.dur}</div>
                <div className="t-s muted">{en ? e.durNoteEn : e.durNote}</div>
              </div>
              <div>
                <div className="cap muted">{tx('Yaş sınırı', 'Age')}</div>
                <div className="v">{en ? e.ageEn : e.age}</div>
                <div className="t-s muted">{tx('Kimlik sorulabilir', 'ID may be checked')}</div>
              </div>
              <div>
                <div className="cap muted">{tx('Kapı açılış', 'Doors')}</div>
                <div className="v">{e.doors}</div>
                <div className="t-s muted">{tx('Geç girişte ara beklenir', 'Latecomers wait for the interval')}</div>
              </div>
              <div>
                <div className="cap muted">{tx('Dil', 'Language')}</div>
                <div className="v">{en ? e.langEn : e.lang}</div>
                <div className="t-s muted">{e.kind === 'Tiyatro' ? tx('İngilizce üstyazı Cmt', 'English surtitles Sat') : '—'}</div>
              </div>
            </div>

            <div className="sec-h">
              <h2 className="d-s">{tx('Seans seç', 'Choose a session')}</h2>
              <span className="muted t-s">
                {e.sessions.length} {tx('seans', 'sessions')}
              </span>
            </div>
            <div className="sessions" role="group" aria-label={tx('Seanslar', 'Sessions')}>
              {e.sessions.map((s) => (
                <button key={s.id} type="button" className="sess" aria-pressed={s.id === sess.id} disabled={s.status === 'sold'} onClick={() => setParams({ s: s.id }, { replace: true })}>
                  <span className="sd">{dayShort(s.startsAt, lang)}</span>
                  <span className="st">{hhmm(s.startsAt)}</span>
                  <span className="ss" style={{ color: s.status === 'few' ? 'var(--t-on)' : s.status === 'sold' ? 'var(--ink-3)' : 'var(--ok)' }}>
                    {seatsLeft[s.status]}
                  </span>
                </button>
              ))}
            </div>

            <div className="sec-h">
              <h2 className="d-s">{tx('Fiyatlar', 'Prices')}</h2>
              <span className="muted t-s">
                {dayShort(sess.startsAt, lang)} · {hhmm(sess.startsAt)}
              </span>
            </div>
            <div className="tiers">
              {TIER_KEYS.map((k) => (
                <div className="tier" key={k}>
                  <span className="sw" style={{ background: TIERS[k].css }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{en ? TIERS[k].en : TIERS[k].tr}</div>
                    <div className="ta">{free(k)}</div>
                  </div>
                  <span className="tp">{tl(e.prices[k], lang)}</span>
                </div>
              ))}
            </div>
            <p className="t-s muted" style={{ margin: '10px 0 0' }}>
              {tx(`Bilet başına ${tl(FEE_KURUS)} hizmet bedeli eklenir.`, `A ${tl(FEE_KURUS, 'en')} service fee is added per ticket.`)}
              {e.venue.wc ? tx(' Tekerlekli sandalye alanı Parter’in son sırasında.', ' Wheelchair spaces are in the last stalls row.') : ''}
            </p>

            <div className="ev-side panel" style={{ marginTop: 24, alignItems: 'center', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <div className="muted t-s">
                  {dayLong(sess.startsAt, lang)}, {hhmm(sess.startsAt)}
                </div>
                <div className="d-s">{en ? `Prices from ${tl(e.fromKurus, 'en')}` : `${tl(e.fromKurus)}’den başlayan fiyatlarla`}</div>
              </div>
              <button type="button" className="btn btn-primary btn-lg" onClick={go} disabled={sess.status === 'sold'}>
                {cta} <Ico n="chev" cls="ico-s" />
              </button>
            </div>

            {e.cast.length > 1 && (
              <>
                <div className="sec-h">
                  <h2 className="d-s">{tx('Oyuncular', 'Cast')}</h2>
                </div>
                <div className="cast">
                  {e.cast.map((c, i) => (
                    <div className="p" key={c}>
                      <div className="av" style={{ background: AV[i % 6] }} aria-hidden="true">
                        {c
                          .split(' ')
                          .map((w) => w[0])
                          .join('')}
                      </div>
                      {c}
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="sec-h">
              <h2 className="d-s">{tx('Hakkında', 'About')}</h2>
            </div>
            <p className="t-l" style={{ margin: 0, maxWidth: '62ch' }}>
              {en ? e.aboutEn : e.about}
            </p>

            <div className="sec-h">
              <h2 className="d-s">{tx('Mekân', 'Venue')}</h2>
            </div>
            <div className="map" role="img" aria-label={`${e.venue.name} ${tx('konumu haritası', 'location map')}`}>
              <svg viewBox="0 0 640 360" preserveAspectRatio="xMidYMid slice" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                <rect width="640" height="360" fill="#E9E2D3" />
                <path d="M0 250 Q160 210 300 260 T640 230 V360 H0Z" fill="#C9D6DD" />
                <g stroke="#FFFDF8" strokeWidth="14" fill="none" strokeLinecap="round">
                  <path d="M40 40 L600 200" />
                  <path d="M120 0 L260 360" />
                  <path d="M420 0 L380 360" />
                </g>
                <g stroke="#FFFDF8" strokeWidth="6" fill="none">
                  <path d="M0 120 L640 90" />
                  <path d="M500 0 L560 260" />
                  <path d="M200 0 Q260 120 180 240" />
                </g>
                <text x="470" y="320" fontFamily="Geist Mono" fontSize="12" fill="#5F7480" letterSpacing="2">
                  {e.venue.area.toLocaleUpperCase('tr-TR')}
                </text>
                <g transform="translate(330 140)">
                  <circle r="26" fill="var(--cobalt)" opacity=".15" />
                  <path d="M0 8 C-12 -4 -14 -10 -14 -16 a14 14 0 0128 0 c0 6 -2 12 -14 24z" fill="var(--cobalt)" />
                  <circle cy="-16" r="5" fill="#fff" />
                </g>
              </svg>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginTop: 14 }}>
              <div>
                <div style={{ fontWeight: 600 }}>{e.venue.name}</div>
                <div className="t-s muted">{e.venue.addr}</div>
                <div className="t-s muted" style={{ marginTop: 4 }}>
                  {en ? e.venue.transitEn : e.venue.transit}
                </div>
              </div>
              <a className="btn btn-line btn-sm" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(e.venue.name + ', ' + e.venue.addr)}`} target="_blank" rel="noreferrer">
                {tx('Yol tarifi', 'Directions')}
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="dock ev-dock">
        <div className="cta-bar">
          <div className="from">
            <span className="t-s muted">
              {dayShort(sess.startsAt, lang)} · {hhmm(sess.startsAt)}
            </span>
            <br />
            <b>{en ? `From ${tl(e.fromKurus, 'en')}` : `${tl(e.fromKurus)}’den`}</b>
          </div>
          <button type="button" className="btn btn-primary btn-lg" onClick={go} disabled={sess.status === 'sold'}>
            {sess.status === 'sold' ? tx('Tükendi', 'Sold out') : cta}
          </button>
        </div>
      </div>
    </>
  );
}
